import React, { ReactNode } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Icon, Toggle } from '.';
import styled, { keyframes } from 'styled-components';
import { theme } from 'src/config';

type StyledAccordionProps = {
  width?:         string;
  height?:        string;
  padding?:       string;
  hasToggle?:     boolean;
  rightElement?:  ReactNode;
  flexDirection?: string;
};

const StyledAccordion = styled(AccordionPrimitive.Root)<StyledAccordionProps>`
  width: ${(p) => p.width || "auto"};
  padding: ${(p) => p.padding || "0"};
  padding-bottom: 2rem;
  /* overflow-y: hidden; */
  height: 100%;

  &:focus-within {
    position: relative;
    z-index: 1;
    box-shadow: none;
  };

`;

type AccordionPrimitiveProps = React.ComponentProps<typeof StyledAccordion>;
type AccordionProps = AccordionPrimitiveProps & StyledAccordionProps;

export const Accordion = React.forwardRef<React.ElementRef<typeof StyledAccordion>, AccordionProps>(
  ({ children, ...props }, forwardedRef) => (
    <StyledAccordion
      ref={forwardedRef}
      {...props}
      {...(props.type === 'single' ? { collapsible: true } : {})}
    >
      {children}
    </StyledAccordion>
  )
);

const StyledItem = styled(AccordionPrimitive.Item)<{$hasBottomBorder?: boolean;}>`
  overflow: hidden;

  &:first-child {
    margin-top: 0;
  };
  
  :last-child button {
    border-bottom: ${(p) => p.$hasBottomBorder ? `1 px solid ${theme.color.secondaryBorder}` : "none"};
  }
`;

const StyledIcon = styled(Icon)`
`;

const StyledTrigger = styled(AccordionPrimitive.Trigger)<StyledAccordionProps>`
  background-color: transparent;
  border: none;
  border-bottom: 1px solid ${theme.color.secondaryBorder};
  padding: 1.5rem 0rem;
  height: fit-content;
  width: 100%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${theme.font.size.small};
  color: ${theme.font.color.slate12};
  /* max-width: 35rem; */
  overflow: hidden;
  

  &[data-state="closed"] {
    ${StyledIcon} {
      transform: rotate(360deg);
      transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
      color: ${theme.color.slate12};
    }
  };

  &[data-state="open"] {
    ${StyledIcon} {
      transform: rotate(180deg);
      transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
      color: ${theme.color.slate12};
    }
  };
  `;

const StyledHeader = styled(AccordionPrimitive.Header)`
  display: flex;
;`


type AccordionTriggerPrimitiveProps = React.ComponentProps<typeof StyledTrigger>;
type AccordionTriggerProps = AccordionTriggerPrimitiveProps & StyledAccordionProps;

export const AccordionTrigger = React.forwardRef<React.ElementRef<typeof StyledTrigger>,
  AccordionTriggerProps
>(({ children, hasToggle, rightElement, ...props }, forwardedRef) => (
    <StyledTrigger {...props} ref={forwardedRef}>
        {children}
      <IconWrapper>
      {rightElement}
      <StyledIcon size="medium" iconId="chevronDown"/>
      {hasToggle && <StyledToggle />}
      </IconWrapper>
    </StyledTrigger>
));

export const AccordionContent = React.forwardRef<React.ElementRef<typeof StyledContent>,
AccordionTriggerProps
>(({ children, ...props }, forwardedRef) => (
  <StyledContent {...props} ref={forwardedRef}>
    {children}
  </StyledContent>
));

const slideDown = keyframes({
  from: { height: 0 },
  to: { height: 'var(--radix-accordion-content-height)' },
});

const slideUp = keyframes({
  from: { height: 'var(--radix-accordion-content-height)' },
  to: { height: 0 },
});

const IconWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const StyledToggle= styled(Toggle)`
  pointer-events: none; 
`


const StyledContent = styled(AccordionPrimitive.Content)`

background: ${theme.color.blackA2};
  overflow: hidden;
  color: ${theme.color.slate12};

  &[data-state="open"] {
    animation: ${slideDown} 0.2s 1 alternate ease-in;
  };

  &[data-state="closed"] {
    animation: ${slideUp} 0.2s 1 alternate ease-out;
  };  
`;



export const AccordionItem = StyledItem;
export const AccordionHeader = StyledHeader;